/**
 * EntitledRoute — gate a premium module behind a plan entitlement.
 *
 * Renders the page when the org's plan includes `feature`, otherwise an upgrade
 * wall whose one press moves the org up a plan, billed at month end (addnow). UX layer only — premium WRITES are (or should be) enforced server-side
 * via require_entitlement; this stops the page rendering and points to upgrade.
 *
 * @since 1.0.0
 */
import { Suspense, lazy, type ReactNode } from 'react';
import { Loader2, Lock } from 'lucide-react';
import { useEntitlements } from '@/shared/hooks/useEntitlements';
import { featureLabel } from '@/shared/lib/entitlements';

// bundletrim: drawn only for a locked feature, so it loads with that page, not the first one.
const UpgradeNowOffer = lazy(() => import('@/shared/components/UpgradeNowOffer').then((m) => ({ default: m.UpgradeNowOffer })));

export function EntitledRoute({ feature, children }: { feature: string; children: ReactNode }) {
    const { has, loading } = useEntitlements();

    if (loading) {
        return (
            <div className="flex items-center justify-center py-24 text-stone-400">
                <Loader2 size={22} className="animate-spin" />
            </div>
        );
    }
    if (has(feature)) return <>{children}</>;

    const label = featureLabel(feature);
    return (
        <div className="flex items-center justify-center px-4 py-20">
            <div className="max-w-md w-full text-center rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 p-8">
                <div className="w-12 h-12 mx-auto flex items-center justify-center mb-4">
                    <Lock size={22} className="text-teal-700" />
                </div>
                <h1 className="text-lg font-semibold text-stone-900 dark:text-stone-100">{label} comes with a higher plan</h1>
                <p className="text-sm text-stone-500 mt-2">
                    Upgrade now and {label} opens straight away for your whole team. You pay with your month-end bill.
                </p>
                {/* addnow: upgrade at once, billed at month end. */}
                <Suspense fallback={null}><UpgradeNowOffer feature={feature} /></Suspense>
            </div>
        </div>
    );
}
